import { useState } from 'react';
import { useRegion } from '../../hooks/queries';
import { useT } from '../../i18n';
import { useCurrentRegion } from '../../stores/region';

/**
 * The city being shown, with a menu to switch to another when there is more than one
 * (closed cities appear for the game owner only, marked "Owner only").
 */
export function RegionSwitcher({ className = '' }: { className?: string }) {
  const t = useT();
  const { data: regions, region } = useRegion();
  const setRegionId = useCurrentRegion((s) => s.setRegionId);
  const [open, setOpen] = useState(false);
  if (!region) return null;
  if (!regions || regions.length < 2) return <span className={className}>{region.name}</span>;
  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 ${className}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="region-switcher"
      >
        {region.name}
        <svg className="size-4 shrink-0 text-ink-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-30 mt-2 min-w-48 overflow-hidden rounded-2xl border border-ink-700 bg-ink-900 py-1 text-sm font-semibold normal-case shadow-xl"
        >
          {regions.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                role="option"
                aria-selected={r.id === region.id}
                onClick={() => {
                  setRegionId(r.id, true);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left font-sans ${r.id === region.id ? 'text-volt' : 'text-ink-100'}`}
                data-testid={`region-option-${r.id}`}
              >
                <span>{r.name}</span>
                {!r.isPublic ? <span className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] uppercase text-ink-300">{t('Owner only')}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
